'use client'

import { cn } from '../lib/utils'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Badge } from './badge'

interface NavBarContextValue {
  value: string
  onSelect: (value: string) => void
}

const NavBarContext = createContext<NavBarContextValue | null>(null)

function useNavBar() {
  const ctx = useContext(NavBarContext)
  if (!ctx) throw new Error('NavBarItem must be used within NavBar')
  return ctx
}

interface NavBar extends React.ComponentProps<'nav'> {
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  /** Hide on scroll down, show on scroll up. Default: true */
  autoHide?: boolean
  /** Scroll threshold in px before toggling. Default: 10 */
  scrollThreshold?: number
}

function NavBar({
  value: controlledValue,
  defaultValue,
  onValueChange,
  autoHide = true,
  scrollThreshold = 10,
  className,
  children,
  ...props
}: NavBar) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '')
  const isControlled = controlledValue !== undefined
  const currentValue = isControlled ? controlledValue : uncontrolledValue

  const onSelect = useCallback(
    (v: string) => {
      if (!isControlled) setUncontrolledValue(v)
      onValueChange?.(v)
    },
    [isControlled, onValueChange],
  )

  const [hidden, setHidden] = useState(false)
  const lastScrollY = useRef(0)
  const navRef = useRef<HTMLElement>(null)

  useEffect((): void | (() => void) => {
    if (!(autoHide && navRef.current)) return

    // Walk up to find the nearest scrollable ancestor (or fall back to window)
    let scrollEl: HTMLElement | null = navRef.current.parentElement
    while (scrollEl && scrollEl !== document.documentElement) {
      const { overflowY } = getComputedStyle(scrollEl)
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        scrollEl.scrollHeight > scrollEl.clientHeight
      ) {
        break
      }
      scrollEl = scrollEl.parentElement
    }

    const target =
      !scrollEl || scrollEl === document.documentElement ? window : scrollEl

    const getScrollTop = () =>
      target === window ? window.scrollY : (target as HTMLElement).scrollTop

    lastScrollY.current = getScrollTop()

    const onScroll = () => {
      const y = getScrollTop()
      if (Math.abs(y - lastScrollY.current) < scrollThreshold) return
      setHidden(y > lastScrollY.current && y > 0)
      lastScrollY.current = y
    }
    target.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      target.removeEventListener('scroll', onScroll)
    }
  }, [autoHide, scrollThreshold])

  const navBarCtxValue = useMemo(
    () => ({ value: currentValue, onSelect }),
    [currentValue, onSelect],
  )

  return (
    <NavBarContext value={navBarCtxValue}>
      <nav
        ref={navRef}
        className={cn(
          'bg-surface-2 fixed inset-x-0 bottom-0 z-50 h-20 transition-transform duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
          hidden && 'translate-y-full',
          className,
        )}
        data-slot="nav-bar"
        {...props}
      >
        <div
          className="medium:justify-center flex h-full items-center justify-around"
          role="tablist"
        >
          {children}
        </div>
      </nav>
    </NavBarContext>
  )
}

interface NavBarItem extends React.ComponentProps<'button'> {
  value: string
  icon: React.ReactNode
  badge?: number
}

function NavBarItem({
  value,
  icon,
  badge,
  children,
  className,
  ...props
}: NavBarItem) {
  const { value: selectedValue, onSelect } = useNavBar()
  const isActive = selectedValue === value

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      data-slot="nav-bar-item"
      data-active={isActive || undefined}
      onClick={() => {
        onSelect(value)
      }}
      className={cn(
        'medium:flex-none medium:w-36 relative flex flex-1 cursor-pointer items-center justify-center transition-colors outline-none select-none',
        'focus-visible:ring-primary/80 focus-visible:rounded-sm focus-visible:ring-2',
        isActive ? 'text-on-surface' : 'text-on-surface-secondary',
        className,
      )}
      {...props}
    >
      {/* Indicator — scales from center, per-item */}
      <span
        className={cn(
          'medium:-top-2 medium:h-10 medium:rounded-[20px] bg-secondary/12 absolute -top-1 h-8 rounded-[16px] transition-[scale,opacity] duration-500 ease-[cubic-bezier(0.2,0,0,1)]',
          'medium:w-[calc(100%_-_16px)] medium:max-w-30 w-14',
          isActive
            ? 'scale-x-100'
            : 'medium:scale-x-100 medium:opacity-0 scale-x-0',
        )}
        data-slot="nav-bar-indicator"
      />
      {/* content wrapper */}
      <span
        className="medium:flex-row relative flex flex-col items-center gap-2"
        data-slot="nav-bar-content"
      >
        <span
          className={cn('relative', isActive && '[&_svg]:fill-current')}
          data-slot="nav-bar-icon"
        >
          {icon}
          {badge !== undefined && (
            <Badge notificationCount={Math.max(1, badge)} />
          )}
        </span>
        <span
          className={cn(
            'text-label-small medium:text-label-medium font-medium',
            // INFO: the ! is a duct tape here (Tailwind issue)
            isActive && 'text-secondary!',
          )}
        >
          {children}
        </span>
      </span>
    </button>
  )
}

export { NavBar, NavBarItem }
